import Response from 'app/api/Response';
import UserApi from 'app/api/UserApi';
import { ReportsList } from 'app/modules/reports/ReportsList';
import { UserStatus } from 'app/types/UserStatus';
import cx from 'classnames';
import { Tab, Tabs } from "../../core/components/Tabs/Tabs";
import { AccessType, RequireAuth, UserRole, useLoginAsMutiation, useMeQuery } from 'legoland-sdk/dist/experimental';
import { Badge } from 'llui';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, HGrid, Heading, useToasts } from 'tombac';
import { ChevronLeftIcon, LoginAsIcon, SaveIcon } from 'tombac-icons';
import { useUserMutation } from "../api/userManagementApiHooks";
import { ContractList, useContractListProps } from "../../contracts/list/ContractList";
import { useFeatureToggle } from "../../feature-flags/useFeatureToggle";
import ActivityHistory from "../../history/ActivityHistory";
import { NoteList } from "../../note/NoteList";
import ProductFormsContainer, { productUpdateFromFormValues } from "../../products/components/ProductFormsContainer";
import UserNotificationsForm from "../../settings/UserNotificationsForm";
import { useUserDetails } from "../UserDetailsProvider";
import { EditUserFormPinnedNotes } from "./EditUserFormPinnedNotes";
import SettingsForm from "./SettingsForm/SettingsForm";
import { UserActions } from "./UserActions";
import { UserApigeeDeveloper } from "./UserApigeeDeveloper";
import UserDetailsForm from "./UserDetailsForm/UserDetailsForm";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const formValuesFromProductDto = products => {
  const values = {};
  products.forEach(p => {
    const expiryDate = p.access.expiryDate || moment(new Date()).add(1, 'month').toDate();
    values[p.id] = {
      id: p.id,
      issue: p.access.issue,
      pending: p.access.status === 'PENDING',
      access: p.access.status === 'GRANTED' || p.access.status === 'PENDING' || p.access.status === 'EXPIRED',
      accessType: p.access.accessType || AccessType.Evaluation,
      apiKey: p.access.apiKey,
      displayName: p.displayName,
      expired: p.access.status === 'EXPIRED',
      expiryDate,
      productType: p.productType,
      resell: p.resell.status === 'ALLOWED',
      resellContract: p.resell.contract || false,
      resellUntil: p.resell.status === 'ALLOWED' && p.resell.expiryDate ? p.resell.expiryDate : expiryDate,
      revoked: p.access.status === 'REVOKED',
      contractAccessId: p.access.contract?.id || false
    };
  });
  return values;
};
const getActiveTab = value => {
  switch (value) {
    case 'products':
      return 0;
    case 'contracts':
      return 1;
    default:
      return 0;
  }
};
export function EditUserForm(_ref) {
  let {
    isLoading
  } = _ref;
  const meQuery = useMeQuery();
  const params = useParams();
  const contractsFeature = useFeatureToggle('contracts');
  const activetab = getActiveTab(params.activetab);
  const {
    mutateAsync: loginAs,
    ...loginAsMutation
  } = useLoginAsMutiation();
  const creatorRole = meQuery.data.me.role;
  const user = useUserDetails();
  const {
    mutateAsync: updateUser
  } = useUserMutation();
  const {
    addToast
  } = useToasts();
  const [details, setDetails] = useState(null);
  const [settings, setSettings] = useState(null);
  const [notifications, setNotifications] = useState(null);
  const [productUpdatePartial, setProductUpdatePartial] = useState(undefined);
  const [productFormValues, setProductFormValues] = useState(null);
  const [savingStatus, setSavingStatus] = useState('idle');
  const [userNotes, setUserNotes] = useState([]);
  const hasUserContracts = user.contracts.length > 0;
  const pinnedNotes = useMemo(() => userNotes.filter(note => note.pinned), [userNotes]);
  useEffect(() => {
    setProductUpdatePartial(undefined);
    if (!user) {
      return;
    }
    setDetails({
      email: user.email,
      role: user.role,
      organization: user.organization && user.organization.id,
      type: user.type,
      pricing: user.pricing
    });
    setSettings({
      ...user.settings
    });
    setNotifications({
      ...user.emailNotifications
    });
    setProductFormValues(formValuesFromProductDto(user.products));
  }, [user]);
  const fetchNotes = useCallback(async () => {
    const response = await UserApi.getUserNotes(user.email);
    const notes = Response.get(response);
    if (!notes) return;
    const sortedNotes = [...notes].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    setUserNotes(sortedNotes);
  }, [user.email]);
  useEffect(() => {
    if (creatorRole === UserRole.Admin || creatorRole === UserRole.Support) {
      fetchNotes();
    }
  }, [creatorRole, fetchNotes]);
  const saveUser = async () => {
    setSavingStatus('loading');
    const userForm = {
      email: details.email,
      role: details.role,
      organization: {
        id: details.organization
      },
      settings: settings,
      emailNotifications: notifications,
      type: details.type,
      pricing: details.pricing,
      ...productUpdatePartial
    };
    const handleSuccess = () => {
      addToast('User saved', 'success');
      setSavingStatus('done');
    };
    const handleError = error => {
      console.error(error);
      addToast(error.message || 'Failed to save user', 'danger');
      setSavingStatus({
        status: 'failed',
        message: error.message
      });
    };
    updateUser({
      update: userForm
    }).then(handleSuccess).catch(handleError);
  };
  const readOnly = creatorRole === UserRole.Support;
  const className = cx('EditUserPage', {
    'EditUserPage--loading': isLoading || savingStatus === 'loading'
  });

  // const status = parseFromUser(user);
  const status = user.status;
  const contractListProps = useContractListProps({
    enableSortBy: true
  });
  return details && settings && /*#__PURE__*/_jsxs("div", {
    className: className,
    children: [/*#__PURE__*/_jsxs("div", {
      className: "EditUserPage__headline",
      children: [/*#__PURE__*/_jsx(Link, {
        to: "/users",
        className: "EditUserPage__back",
        children: /*#__PURE__*/_jsx("span", {
          className: "EditUserPage__headline-icon",
          children: /*#__PURE__*/_jsx(ChevronLeftIcon, {
            size: "lg"
          })
        })
      }), /*#__PURE__*/_jsxs("div", {
        className: "EditUserPage__headline-user",
        children: [/*#__PURE__*/_jsx(Heading, {
          as: "h2",
          className: "EditUserPage__headline-text",
          level: 3,
          children: user.email
        }), /*#__PURE__*/_jsx("div", {
          className: "EditUserPage__user-status",
          children: {
            [UserStatus.Active]: /*#__PURE__*/_jsx(Badge, {
              type: "primary",
              children: "Active"
            }),
            [UserStatus.Deleted]: /*#__PURE__*/_jsx(Badge, {
              type: "danger",
              children: "Deleted"
            }),
            [UserStatus.Inactive]: /*#__PURE__*/_jsx(Badge, {
              children: "Inactive"
            }),
            [UserStatus.Locked]: /*#__PURE__*/_jsx(Badge, {
              children: "Locked"
            })
          }[status]
        }), user && /*#__PURE__*/_jsx(RequireAuth, {
          role: [UserRole.Admin, UserRole.Support],
          children: /*#__PURE__*/_jsx(UserApigeeDeveloper, {
            user: user
          })
        })]
      }), /*#__PURE__*/_jsxs(HGrid, {
        ml: "auto",
        children: [!readOnly && /*#__PURE__*/_jsx(Button, {
          busy: savingStatus === 'loading',
          prepend: /*#__PURE__*/_jsx(SaveIcon, {}),
          onClick: saveUser,
          variant: "primary",
          children: savingStatus === 'loading' ? 'Saving...' : 'Save'
        }), /*#__PURE__*/_jsx(RequireAuth, {
          role: [UserRole.Admin, UserRole.Support],
          children: status === UserStatus.Active && (creatorRole === UserRole.Admin || user.role !== UserRole.Admin && user.role !== UserRole.Support) && /*#__PURE__*/_jsx(Button, {
            busy: loginAsMutation.isLoading,
            onClick: () => {
              loginAs({
                username: user.email
              }).then(() => {
                window.location.assign('/dashboard');
              });
            },
            prepend: /*#__PURE__*/_jsx(LoginAsIcon, {
              size: "sm"
            }),
            children: "Login as"
          })
        }), !readOnly && /*#__PURE__*/_jsx(UserActions, {
          user: user
        })]
      })]
    }), /*#__PURE__*/_jsxs("div", {
      className: "EditUserPage__section-container",
      children: [/*#__PURE__*/_jsxs("div", {
        className: "EditUserPage__section EditUserPage__user-details",
        style: {
          gridArea: 'user'
        },
        children: [/*#__PURE__*/_jsx(Heading, {
          level: 3,
          children: "User"
        }), /*#__PURE__*/_jsx(UserDetailsForm, {
          defaultValues: details,
          hasUserContracts: hasUserContracts,
          onChange: setDetails,
          wide: true
        })]
      }), /*#__PURE__*/_jsx(EditUserFormPinnedNotes, {
        pinnedNotes: pinnedNotes
      }), /*#__PURE__*/_jsx("div", {
        className: "EditUserPage__section EditUserPage__products",
        style: {
          gridArea: 'products'
        },
        children: contractsFeature.enabled && (creatorRole === UserRole.Admin || creatorRole === UserRole.Support) ? /*#__PURE__*/_jsxs(Tabs, {
          initialTabIndex: activetab,
          children: [/*#__PURE__*/_jsx(Tab, {
            tabContent: /*#__PURE__*/_jsx("div", {
              className: 'DashboardContactModal_TabContent',
              children: /*#__PURE__*/_jsx("div", {
                className: 'DashboardContactModal_TabContent_label',
                children: "Products & Packages"
              })
            }),
            children: /*#__PURE__*/_jsx(ProductFormsContainer, {
              initialValues: productFormValues,
              onChange: values => setProductUpdatePartial(productUpdateFromFormValues(values)),
              userRole: details.role,
              hasUserContracts: hasUserContracts
            })
          }), /*#__PURE__*/_jsx(Tab, {
            tabContent: /*#__PURE__*/_jsx("div", {
              className: 'DashboardContactModal_TabContent',
              children: /*#__PURE__*/_jsx("div", {
                className: 'DashboardContactModal_TabContent_label',
                children: "Contracts"
              })
            }),
            children: /*#__PURE__*/_jsx(ContractList, {
              variant: "narrow",
              filters: {
                user: user.email
              },
              ...contractListProps
            })
          }), /*#__PURE__*/_jsx(Tab, {
            tabContent: /*#__PURE__*/_jsx("div", {
              className: 'DashboardContactModal_TabContent',
              children: /*#__PURE__*/_jsx("div", {
                className: 'DashboardContactModal_TabContent_label',
                children: "Apps data"
              })
            }),
            children: /*#__PURE__*/_jsx(ReportsList, {
              user: user,
              creatorRole: creatorRole
            })
          }), /*#__PURE__*/_jsx(Tab, {
            tabContent: /*#__PURE__*/_jsx("div", {
              className: 'DashboardContactModal_TabContent',
              children: /*#__PURE__*/_jsx("div", {
                className: 'DashboardContactModal_TabContent_label',
                children: "Settings"
              })
            }),
            children: /*#__PURE__*/_jsxs("div", {
              className: "EditUserPage__settings-container",
              children: [/*#__PURE__*/_jsxs("div", {
                className: "EditUserPage__section EditUserPage__settings",
                style: {
                  gridArea: 'settings'
                },
                children: [/*#__PURE__*/_jsx(Heading, {
                  level: 3,
                  children: "Settings"
                }), /*#__PURE__*/_jsx(SettingsForm, {
                  defaultValue: settings,
                  onChange: setSettings
                })]
              }), /*#__PURE__*/_jsxs("div", {
                className: "EditUserPage__section EditUserPage__settings",
                style: {
                  gridArea: 'notifications'
                },
                children: [/*#__PURE__*/_jsx(Heading, {
                  level: 3,
                  children: "E-mail notifications"
                }), /*#__PURE__*/_jsx(UserNotificationsForm, {
                  notifications: user.emailNotifications,
                  onChange: setNotifications
                })]
              })]
            })
          }), (creatorRole === UserRole.Admin || creatorRole === UserRole.Support) && /*#__PURE__*/_jsx(Tab, {
            tabContent: /*#__PURE__*/_jsx("div", {
              className: 'DashboardContactModal_TabContent',
              children: /*#__PURE__*/_jsxs("div", {
                className: 'DashboardContactModal_TabContent_label',
                children: ["Notes (", userNotes.length, ")"]
              })
            }),
            children: /*#__PURE__*/_jsx(NoteList, {
              notes: userNotes,
              role: creatorRole,
              email: user.email,
              fetchNotes: fetchNotes
            })
          })]
        }) : /*#__PURE__*/_jsx(ProductFormsContainer, {
          initialValues: productFormValues,
          onChange: values => setProductUpdatePartial(productUpdateFromFormValues(values)),
          userRole: details.role,
          hasUserContracts: hasUserContracts
        })
      }), (creatorRole === UserRole.Admin || creatorRole === UserRole.Support) && /*#__PURE__*/_jsxs("div", {
        className: "EditUserPage__section EditUserPage__history",
        style: {
          gridArea: 'history'
        },
        children: [/*#__PURE__*/_jsx("h3", {
          children: "History"
        }), /*#__PURE__*/_jsx(ActivityHistory, {
          email: user.email
        })]
      })]
    })]
  });
}